import { defineComponent as _defineComponent } from 'vue'
import Breadcrumbs from "@/components/ui/Breadcrumbs";
  import LotDeclineModal from "@/components/ui/modals/LotDeclineModal.vue";
  import LotApproveButton from "@/components/Lot/LotApproveButton.vue";
  import EoiDeclineRefundModal from "@/components/ui/modals/EoiDeclineRefundModal.vue";
  import DocumentItem from "@/components/ui/DocumentItem.vue";
  import DeletedUserBlock from "@/components/ui/DeletedUserBlock.vue";
  import { Breadcrumb } from "@/types";
  import { inject, ref, computed, onMounted, watch, nextTick } from "vue";
  import SuccessDocusignModal from "../ui/modals/SuccessDocusignModal.vue";
  import router from "@/router";
  import { useStore } from "vuex";
  import { EoiInterface } from "@/types/EoiInterface";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { formatDate } from "@/helpers/date";
  import { getLotStatusClass } from "@/helpers/status";
  import { DocumentInterface } from "@/types/DocumentInterface";
  import { formatPrice, formatPriceTrim } from "@/helpers/transformation/price";
  import { getEoiStatusClass } from "@/helpers/status";
  import { getStatusText } from "@/helpers/eoi";
  import { useToast } from "vue-toastification";
  import EoiApi from "@/services/api/eoiApi";
  import { copyToClipboard } from "@/helpers/copyToClipboard";
  import AttentionModal from "@/components/ui/modals/AttentionModal.vue";
  import { LotStatus } from "@/enums/LotStatus";
  import { removeEmptyEntries } from "@/helpers/removeEmptyEntries";
  import CompletedResForm from "@/components/ui/modals/CompletedResForm.vue";
  import SendEoiDocumentsModal from "@/components/ui/modals/SendEoiDocumentsModal.vue";
  import SelectReiTemplate from "../ui/SelectReiTemplate.vue";
  import { EoiFinanceStatus } from "@/enums/EoiFinanceStatus";
  import GenerateContractFieldsModal from "../ui/modals/GenerateContractFieldsModal.vue";
  import SelectOptionToGenerateContractModal from "../ui/modals/SelectOptionToGenerateContractModal.vue";
  import { LandownerNavigationSlugs } from "@/enums/LandownerNavigationSlugs";
  import { DocusignUserData } from "@/types/DocusignUserData";
  import { useDocusignAuth } from "@/features/docusign/useDocusignAuth";
  import { StatesEnum } from "@/enums/StatesEnum";
  import { isLotOwner } from "@/helpers/lot/isLotOwner";
  import { LotInterface } from "@/types/LotInterface";
  import { AxiosResponse } from "axios";
  import { Business } from "@/types/BusinessInterface";

  const paymentLink = "https://dashboard.stripe.com/payments/";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'EOIDetails',
  setup(__props, { expose: __expose }) {
  __expose();

  const toast = useToast();
  const store = useStore();
  const eoiId = router.currentRoute.value.params.id;
  const buyerName = ref("");
  const isPaymentAllowed = ref(true);
  const showAttentionModal = ref(false);
  const downloadBtnLoading = ref(false);
  const openCompletedResForm = ref(false);
  const openSendEoiDocumentsModal = ref(false);
  const isSelectReiTemplateModalOpen = ref(false);
  const isDocusignEnabled = ref(false);
  const reiToken = ref(store.state.rei.userData.accessToken);
  const isLoggedRei = ref(false);
  const isSuccessModalOpen = ref(false);
  const isDocusignFieldsModalOpen = ref(false);
  const isSelectOptionToGenerateContractModal = ref(false);
  const isPurchasedByCompany = ref(false);
  const userBusiness = ref<Business | null>(null);
  const backButtonPath = `/${LandownerNavigationSlugs.EOI}`;
  const openSelectOptionToGenerateModal = () => {
    openCompletedResForm.value = false;
    isSelectOptionToGenerateContractModal.value = true;
  };
  const getFirstBuyerPostcode = (postcode: string, state: string) => {
    const statesList = Object.values(StatesEnum) as string[];

    if (!postcode && !state) return null;

    if (postcode.length === 4) return postcode;

    if (!statesList.includes(state) && state.length === 4) return state;

    return null;
  };

  const getUserBusiness = () => {
    loading.value = true;

    store
      .dispatch("business/getBusinessByLandownerId")
      .then((res: AxiosResponse<Business>) => {
        userBusiness.value = res.data;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const canManageLot = computed<boolean>(() => {
    return isLotOwner(eoi.value?.lotId as LotInterface, userBusiness.value);
  });

  const isSameBusinessOwner = computed<boolean>(() => {
    const eoiBusinessId = eoi.value?.businessId?._id;

    if (eoiBusinessId) {
      return eoiBusinessId === userBusiness.value?._id;
    }

    const lot = eoi.value?.lotId;

    if (!lot?.ownershipId) {
      return true;
    }

    return canManageLot.value;
  });

  const getFirstBuyerState = (state: string) => {
    const statesList = Object.values(StatesEnum).map(String);

    return statesList.includes(state) ? state : null;
  };
  const setReiUserData = () => {
    if (reiToken.value) {
      isLoggedRei.value = true;
      return true;
    }

    store.commit("rei/setUserData", null);
    return false;
  };

  const crumbs = computed(() => {
    return [
      {
        label: "Contract Information Form",
        path: "/eoi",
      },
      {
        label: `Contract Information Form ${
          buyerName.value ? " - " + buyerName.value : ""
        }`,
      },
    ];
  });
  const eoi = ref<EoiInterface | null>(null);

  const amlFieldLabels: Record<string, string> = {
    dateOfBirth: "Date of Birth",
    nationality: "Nationality",
    countryOfResidence: "Country of Residence",
    occupation: "Occupation",
    annualIncome: "Annual Income",
    sourceOfFunds: "Source of Funds",
    sourceOfFundsOther: "Source of Funds – Other details",
    sourceOfDepositFunds: "Source of Deposit Funds",
    sourceOfDepositFundsOther: "Source of Deposit Funds – Other details",
    abn: "ABN",
    acn: "ACN",
    soleTraderBusinessName: "Sole Trader Business Name",
    registeredBusinessName: "Registered Business Name",
    tradingName: "Trading Name",
    dateBusinessStarted: "Date Business Started",
    incorporationDate: "Incorporation Date",
    websiteUrl: "Website URL",
    foreignCompany: "Is this a Foreign Company?",
    trustSmsfType: "Trust / SMSF Type",
    trustSmsfTypeOther: "Trust / SMSF Type – Other details",
    trustName: "Trust Name",
    establishmentDate: "Establishment Date",
    partnershipType: "Partnership Type",
    abnRegistrationDate: "ABN Registration Date",
  };

  const amlFieldsByPurchaserType: Record<string, string[]> = {
    Individual: [
      "dateOfBirth",
      "nationality",
      "countryOfResidence",
      "occupation",
      "annualIncome",
      "sourceOfFunds",
      "sourceOfFundsOther",
      "sourceOfDepositFunds",
      "sourceOfDepositFundsOther",
    ],
    "Sole Trader": [
      "dateOfBirth",
      "nationality",
      "countryOfResidence",
      "occupation",
      "annualIncome",
      "sourceOfFunds",
      "sourceOfFundsOther",
      "sourceOfDepositFunds",
      "sourceOfDepositFundsOther",
      "abn",
      "soleTraderBusinessName",
      "tradingName",
      "dateBusinessStarted",
      "websiteUrl",
    ],
    Company: [
      "foreignCompany",
      "abn",
      "acn",
      "registeredBusinessName",
      "tradingName",
      "incorporationDate",
      "websiteUrl",
    ],
    "Trust / SMSF": [
      "trustSmsfType",
      "trustSmsfTypeOther",
      "abn",
      "acn",
      "trustName",
      "tradingName",
      "establishmentDate",
    ],
    Partnership: [
      "partnershipType",
      "abn",
      "abnRegistrationDate",
      "registeredBusinessName",
      "tradingName",
      "websiteUrl",
    ],
  };

  const amlDisplayRows = computed(() => {
    const amlDetails = eoi.value?.amlDetails;

    if (!amlDetails) return [];

    const purchaserType = eoi.value?.purchaserType || "";
    const permittedFields = amlFieldsByPurchaserType[purchaserType] || [];

    const rows: Array<{ heading?: string; label?: string; value?: string }> = [
      { label: "Purchaser Type", value: purchaserType || "-" },
    ];

    const addDetails = (
      heading: string | undefined,
      details?: Record<string, string | boolean>
    ) => {
      if (!details) return;

      const detailRows = permittedFields.flatMap((key) => {
        const value = details[key];

        if (value === "" || value === undefined || value === null) return [];

        return [
          {
            label: amlFieldLabels[key] || key,
            value:
              key === "annualIncome" && typeof value !== "boolean"
                ? formatPriceTrim(value)
                : typeof value === "boolean"
                ? value
                  ? "Yes"
                  : "No"
                : String(value),
          },
        ];
      });

      if (!detailRows.length) return;

      if (heading) rows.push({ heading });

      rows.push(...detailRows);
    };

    if (["Individual", "Sole Trader"].includes(purchaserType)) {
      const hasSecondBuyer = Boolean(eoi.value?.buyersData?.secondBuyer);

      addDetails(hasSecondBuyer ? "Buyer 1" : undefined, amlDetails.firstBuyer);

      if (hasSecondBuyer) {
        addDetails("Buyer 2", amlDetails.secondBuyer);
      }
    } else {
      addDetails(undefined, amlDetails.entity);
    }

    return rows;
  });

  const documentsArray = ref<DocumentInterface[]>([]);
  const estateHasCollaborators = computed(() => {
    const collaboratorsLength =
      eoi.value?.estateId?.collaboratorBusiness?.length;

    return collaboratorsLength && collaboratorsLength > 0;
  });

  onMounted(() => {
    getEoi();
    store.dispatch("appSettings/getAppSettings").then((res) => {
      isPaymentAllowed.value = res?.data?.isPaymentAllowed;
    });
    getUserBusiness();
    getDocusignEnabled();
    setReiUserData();
  });

  onMounted(() => {
    const openGenerateContract = sessionStorage.getItem("openGenerateContract");

    if (
      store.state.docusign.userData.isLogged &&
      Boolean(openGenerateContract)
    ) {
      onGenerateContract();
    }
  });

  const onGenerateContract = async () => {
    const isLogged = store.state.docusign.userData.isLogged;

    if (!isLogged) {
      sessionStorage.setItem("eoiId", `${eoiId}`);
      sessionStorage.setItem("openGenerateContract", "true");
      return window.open(`${process.env.VUE_APP_API_URL}docusign`, "_self");
    }

    isSelectOptionToGenerateContractModal.value = false;
    await nextTick(() => (isDocusignFieldsModalOpen.value = true));
  };

  const onGenerateReiContract = () => {
    if (!isLoggedRei.value) {
      sessionStorage.setItem("eoiId", `${eoiId}`);
      return window.open(`${process.env.VUE_APP_API_URL}rei`, "_self");
    }

    isSelectReiTemplateModalOpen.value = true;
    return true;
  };

  const deletedUser = ref(false);
  const loading = ref(false);
  const onDepositeRefunded = () => {
    EoiApi.updateEoi(eoiId, { status: EoiStatus.REFUNDED }).then(() => {
      getEoi();
    });
  };

  const getEoi = () => {
    loading.value = true;
    store
      .dispatch("eoi/getEoi", eoiId)
      .then((res) => {
        eoi.value = res.data;

        if (res.data?.companyDetails) {
          isPurchasedByCompany.value = Object.values(
            res.data?.companyDetails
          ).some((value) => value);
        } else {
          isPurchasedByCompany.value = false;
        }

        if (
          eoi.value?.contractConditions?.financeBrokerDetails &&
          !Object.keys(
            removeEmptyEntries(
              eoi.value?.contractConditions?.financeBrokerDetails
            )
          ).length
        ) {
          eoi.value.contractConditions.financeBrokerDetails = null;
        }

        if (!eoi.value?.customerId) {
          deletedUser.value = true;
          return;
        }

        // TODO: check if invoices are available in the new payment system
        // getInvoices();
        buyerName.value = `${eoi.value?.buyersData?.firstBuyer?.firstName} ${eoi.value?.buyersData?.firstBuyer?.lastName}`;

        if (eoi.value) {
          documentsArray.value = [];

          if (eoi.value.buyersData?.firstBuyer?.documents?.length > 0) {
            documentsArray.value.push(
              ...eoi.value.buyersData.firstBuyer.documents
            );
          }

          if (eoi.value.buyersData?.secondBuyer?.documents?.length > 0) {
            documentsArray.value.push(
              ...eoi.value.buyersData.secondBuyer.documents
            );
          }

          if (
            eoi.value.contractConditions?.bankApprovalDocument &&
            eoi.value.contractConditions?.bankApprovalDocument?.link
          ) {
            documentsArray.value.push(
              eoi.value.contractConditions.bankApprovalDocument
            );
          }
        }

        lotInfo.value = {
          lot: eoi.value?.lotId.title,
          name:
            eoi.value?.buyersData?.firstBuyer?.firstName +
            " " +
            eoi.value?.buyersData?.firstBuyer?.lastName,
          eoiId: eoi.value?._id,
          lotId: eoi.value?.lotId._id,
        };
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const invoices = ref([]);
  const refundLink = ref("");
  const openGenerateContract = computed(() => {
    return sessionStorage.getItem("openGenerateContract");
  });

  useDocusignAuth();

  function getInvoices() {
    if (!eoi.value?.customerId) {
      return;
    }

    store
      .dispatch("eoi/getInvoice", {
        eoiId: eoi.value?._id,
        customerId: eoi.value?.customerId?._id,
        landownerId: eoi.value?.landownerId,
        lotId: eoi.value?.lotId._id,
      })
      .then((res) => {
        invoices.value = res?.data;

        if (invoices.value.length > 0) {
          refundLink.value =
            paymentLink +
            (invoices.value[0].data?.object?.payment_intent || "");
        }
      });
  }

  const lotInfo = ref({});

  const isOpenDeclineModal = ref(false);
  const onButtonDecline = () => {
    if (!isPaymentAllowed.value) {
      showAttentionModal.value = true;
    } else {
      isOpenDeclineModal.value = true;
    }
  };

  function onCloseDeclineModal() {
    isOpenDeclineModal.value = false;
  }

  const onDeclineLoading = () => {
    declineLoading.value = true;
  };

  const declineLoading = ref(false);

  function onDecline() {
    declineLoading.value = false;
    getEoi();
  }

  const onConfirm = () => {
    getEoi();
    openCompletedResForm.value = true;
  };

  const closeCompletedResForm = () => {
    openCompletedResForm.value = false;
  };

  function zeroCheck(value: string, units: string) {
    return +value ? `${value} ${units}` : "Irregular";
  }

  const isEoiDeclineRefundModal = ref(false);

  function refundDecline() {
    isEoiDeclineRefundModal.value = true;
  }
  function onCloseRefundDeclineModal() {
    isEoiDeclineRefundModal.value = false;
  }

  function onRefundDeclineModal() {
    isEoiDeclineRefundModal.value = false;
    getEoi();
  }

  const getPaymentUrl = () => {
    store.dispatch("eoi/getPaymentUrl", eoiId).then((res) => {
      copyToClipboard(res.data, "New payment link was copied to clipboard");
    });
  };

  const getContractConditionsFields = (field: string) => {
    const contractConditions = eoi.value?.contractConditions;

    return contractConditions && field in contractConditions ? true : false;
  };

  const getSalesAdviceDocument = () => {
    downloadBtnLoading.value = true;
    store
      .dispatch("eoi/downloadPdf", eoiId)
      .then((res) => {
        window.open(res.data, "_blank");
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => (downloadBtnLoading.value = false));
  };

  const getDocusignEnabled = () => {
    store
      .dispatch("estate/getEstate", store.state.user.selectedEstateId)
      .then((res) => {
        isDocusignEnabled.value = res.data.showDocusign;
      });
  };

const __returned__ = { toast, store, eoiId, buyerName, isPaymentAllowed, showAttentionModal, downloadBtnLoading, openCompletedResForm, openSendEoiDocumentsModal, isSelectReiTemplateModalOpen, isDocusignEnabled, reiToken, isLoggedRei, isSuccessModalOpen, isDocusignFieldsModalOpen, isSelectOptionToGenerateContractModal, isPurchasedByCompany, userBusiness, backButtonPath, openSelectOptionToGenerateModal, getFirstBuyerPostcode, getUserBusiness, canManageLot, isSameBusinessOwner, getFirstBuyerState, setReiUserData, crumbs, eoi, amlFieldLabels, amlFieldsByPurchaserType, amlDisplayRows, documentsArray, estateHasCollaborators, onGenerateContract, onGenerateReiContract, deletedUser, loading, onDepositeRefunded, getEoi, invoices, paymentLink, refundLink, openGenerateContract, getInvoices, lotInfo, isOpenDeclineModal, onButtonDecline, onCloseDeclineModal, onDeclineLoading, declineLoading, onDecline, onConfirm, closeCompletedResForm, zeroCheck, isEoiDeclineRefundModal, refundDecline, onCloseRefundDeclineModal, onRefundDeclineModal, getPaymentUrl, getContractConditionsFields, getSalesAdviceDocument, getDocusignEnabled, get Breadcrumbs() { return Breadcrumbs }, LotDeclineModal, LotApproveButton, EoiDeclineRefundModal, DocumentItem, DeletedUserBlock, get Breadcrumb() { return Breadcrumb }, inject, ref, computed, onMounted, watch, nextTick, SuccessDocusignModal, get router() { return router }, get useStore() { return useStore }, get EoiInterface() { return EoiInterface }, get EoiStatus() { return EoiStatus }, get formatDate() { return formatDate }, get getLotStatusClass() { return getLotStatusClass }, get DocumentInterface() { return DocumentInterface }, get formatPrice() { return formatPrice }, get formatPriceTrim() { return formatPriceTrim }, get getEoiStatusClass() { return getEoiStatusClass }, get getStatusText() { return getStatusText }, get useToast() { return useToast }, get EoiApi() { return EoiApi }, get copyToClipboard() { return copyToClipboard }, AttentionModal, get LotStatus() { return LotStatus }, get removeEmptyEntries() { return removeEmptyEntries }, CompletedResForm, SendEoiDocumentsModal, SelectReiTemplate, get EoiFinanceStatus() { return EoiFinanceStatus }, GenerateContractFieldsModal, SelectOptionToGenerateContractModal, get LandownerNavigationSlugs() { return LandownerNavigationSlugs }, get DocusignUserData() { return DocusignUserData }, get useDocusignAuth() { return useDocusignAuth }, get StatesEnum() { return StatesEnum }, get isLotOwner() { return isLotOwner }, get LotInterface() { return LotInterface }, get AxiosResponse() { return AxiosResponse }, get Business() { return Business } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})